// mindmetal-app/web/src/pages/employee/ApiExplorer.tsx
import { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { Terminal, Clock, Trash2 } from 'lucide-react'
import { Card, CardHeader, CardTitle, CardContent } from '@personic/shared'
import {
  ApiRequestBuilder,
  type HttpMethod,
} from '@/components/employee/ApiRequestBuilder'
import { ResponseViewer } from '@/components/employee/ResponseViewer'
import { useApiRequest, type ApiResponse } from '@/hooks/useDevTools'

interface RequestHistoryItem {
  id: string
  timestamp: Date
  method: HttpMethod
  endpoint: string
  status: number
  responseTime: number
}

const PRESET_ENDPOINTS = [
  { value: '/health', label: 'Health Check' },
  { value: '/v3/profiles', label: 'List Profiles' },
  { value: '/v3/profiles/{canonical_id}', label: 'Get Profile' },
  { value: '/v3/profiles/{canonical_id}/distill', label: 'Run Distillation (POST)' },
  { value: '/v3/profiles/{canonical_id}/persona', label: 'Generate Persona (POST)' },
  { value: '/tenants', label: 'List Tenants' },
  { value: '/tenants/{id}', label: 'Get Tenant' },
  { value: '/usage/stats', label: 'Usage Stats' },
  { value: '/admin/health/services', label: 'Service Health' },
  { value: '/admin/health/metrics', label: 'System Metrics' },
]

const STORAGE_KEY = 'devtools-api-history'

export default function ApiExplorer() {
  const [method, setMethod] = useState<HttpMethod>('GET')
  const [endpoint, setEndpoint] = useState('')
  const [headers, setHeaders] = useState<{ key: string; value: string }[]>([])
  const [body, setBody] = useState('')
  const [response, setResponse] = useState<ApiResponse | null>(null)
  const [history, setHistory] = useState<RequestHistoryItem[]>([])

  const apiRequest = useApiRequest()

  // Load history from localStorage
  useEffect(() => {
    const saved = localStorage.getItem(STORAGE_KEY)
    if (saved) {
      try {
        const parsed = JSON.parse(saved)
        setHistory(
          parsed.map((item: RequestHistoryItem) => ({
            ...item,
            timestamp: new Date(item.timestamp),
          }))
        )
      } catch {
        // Invalid data, ignore
      }
    }
  }, [])

  // Save history to localStorage
  const saveHistory = (items: RequestHistoryItem[]) => {
    setHistory(items)
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items))
  }

  const handleSend = async () => {
    if (!endpoint) return

    const headerObj: Record<string, string> = {}
    headers.forEach((h) => {
      if (h.key && h.value) {
        headerObj[h.key] = h.value
      }
    })

    try {
      const result = await apiRequest.mutateAsync({
        method,
        endpoint,
        headers: headerObj,
        body: body || undefined,
      })

      setResponse(result)

      // Add to history
      const newHistoryItem: RequestHistoryItem = {
        id: Date.now().toString(),
        timestamp: new Date(),
        method,
        endpoint,
        status: result.status,
        responseTime: result.responseTime,
      }

      const newHistory = [newHistoryItem, ...history.slice(0, 9)]
      saveHistory(newHistory)
    } catch (error) {
      console.error('Request failed:', error)
    }
  }

  const loadFromHistory = (item: RequestHistoryItem) => {
    setMethod(item.method)
    setEndpoint(item.endpoint)
  }

  const clearHistory = () => {
    saveHistory([])
  }

  const getStatusColor = (status: number) => {
    if (status >= 200 && status < 300) return 'text-emerald-600'
    if (status >= 300 && status < 400) return 'text-blue-600'
    if (status >= 400 && status < 500) return 'text-amber-600'
    return 'text-red-600'
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex items-center gap-4"
      >
        <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-blue-700 flex items-center justify-center">
          <Terminal className="w-6 h-6 text-white" />
        </div>
        <div>
          <h1 className="text-2xl font-display font-bold text-midnight-900">API Explorer</h1>
          <p className="text-warm-500">Test and debug API endpoints</p>
        </div>
      </motion.div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Request Builder */}
        <div className="lg:col-span-2 space-y-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            <Card>
              <CardHeader>
                <CardTitle>Request</CardTitle>
              </CardHeader>
              <CardContent>
                <ApiRequestBuilder
                  method={method}
                  onMethodChange={setMethod}
                  endpoint={endpoint}
                  onEndpointChange={setEndpoint}
                  headers={headers}
                  onHeadersChange={setHeaders}
                  body={body}
                  onBodyChange={setBody}
                  onSend={handleSend}
                  isLoading={apiRequest.isPending}
                  endpoints={PRESET_ENDPOINTS}
                />
              </CardContent>
            </Card>
          </motion.div>

          {/* Response */}
          {response && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <Card>
                <CardHeader>
                  <CardTitle>Response</CardTitle>
                </CardHeader>
                <CardContent>
                  <ResponseViewer
                    status={response.status}
                    statusText={response.statusText}
                    headers={response.headers}
                    body={response.body}
                    responseTime={response.responseTime}
                  />
                </CardContent>
              </Card>
            </motion.div>
          )}
        </div>

        {/* History Sidebar */}
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.2 }}
        >
          <Card>
            <CardHeader className="flex-row items-center justify-between">
              <CardTitle className="text-base">Request History</CardTitle>
              {history.length > 0 && (
                <button
                  onClick={clearHistory}
                  className="text-warm-400 hover:text-red-500 transition-colors"
                  title="Clear history"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              )}
            </CardHeader>
            <CardContent>
              {history.length === 0 ? (
                <p className="text-sm text-warm-400 text-center py-4">No requests yet</p>
              ) : (
                <div className="space-y-2">
                  {history.map((item) => (
                    <button
                      key={item.id}
                      onClick={() => loadFromHistory(item)}
                      className="w-full text-left p-3 rounded-lg border border-warm-200 hover:border-copper-300 hover:bg-copper-50 transition-all"
                    >
                      <div className="flex items-center gap-2 mb-1">
                        <span
                          className={`text-xs font-mono font-semibold px-1.5 py-0.5 rounded ${
                            item.method === 'GET'
                              ? 'bg-emerald-100 text-emerald-700'
                              : item.method === 'POST'
                                ? 'bg-blue-100 text-blue-700'
                                : item.method === 'PUT'
                                  ? 'bg-amber-100 text-amber-700'
                                  : item.method === 'DELETE'
                                    ? 'bg-red-100 text-red-700'
                                    : 'bg-purple-100 text-purple-700'
                          }`}
                        >
                          {item.method}
                        </span>
                        <span className={`text-xs font-mono ${getStatusColor(item.status)}`}>
                          {item.status}
                        </span>
                      </div>
                      <p className="text-sm font-mono text-midnight-700 truncate">{item.endpoint}</p>
                      <div className="flex items-center gap-2 mt-1 text-xs text-warm-400">
                        <Clock className="w-3 h-3" />
                        {item.responseTime}ms
                        <span className="text-warm-300">|</span>
                        {item.timestamp.toLocaleTimeString()}
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </div>
  )
}
